import React, { useState } from 'react';
import { ChipOwnProps, Menu, MenuItem, SxProps } from '@mui/material';
import { useUiTheme } from 'framework/uiTheme';
import { Badge, SmallLoader, Tooltip } from '../../mui-builder';

export interface EditableBadgeValueProps {
  value: string;
  bgcolor?: string;
  disabled?: boolean;
  disabledReason?: JSX.Element;
  textColor?: string;
}

interface EditableBadgeProps {
  children: any;
  color: ChipOwnProps['color'];
  values: EditableBadgeValueProps[];
  canUpdate: boolean;
  onChange: (newValue: string) => Promise<void>;
  menuItemSx?: SxProps;
  badgeSx?: SxProps;
}

const EditableBadge = ({
  children,
  color,
  values,
  onChange,
  canUpdate = true,
  menuItemSx = {},
  badgeSx,
}: EditableBadgeProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const {
    Badge: ThemedBadge = Badge,
    SmallLoader: ThemedSmallLoader = SmallLoader,
    Tooltip: ThemedTooltip = Tooltip,
  } = useUiTheme();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = async (newValue: string) => {
    try {
      setLoading(true);
      await onChange(newValue);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const menuItems = values.map(({ value, bgcolor, textColor, disabled = false, disabledReason }) => {
    if (disabled && disabledReason) {
      return (
        <ThemedTooltip key={value} title={disabledReason}>
          <span>
            <MenuItem
              key={value}
              onClick={() => handleMenuItemClick(value)}
              sx={{
                ...menuItemSx,
                ...(bgcolor && {
                  backgroundColor: bgcolor,
                  '&:hover': {
                    backgroundColor: bgcolor,
                    opacity: '0.8',
                  },
                }),
                ...(textColor && {
                  color: textColor,
                }),
              }}
              disabled={disabled}
            >
              {value}
            </MenuItem>
          </span>
        </ThemedTooltip>
      );
    } else {
      return (
        <MenuItem
          key={value}
          onClick={() => handleMenuItemClick(value)}
          sx={{
            ...menuItemSx,
            ...(bgcolor && {
              backgroundColor: bgcolor,
              '&:hover': {
                backgroundColor: bgcolor,
                opacity: '0.8',
              },
            }),
            ...(textColor && {
              color: textColor,
            }),
          }}
          disabled={disabled}
        >
          {value}
        </MenuItem>
      );
    }
  });

  return (
    <>
      <ThemedBadge color={color} onClick={canUpdate ? handleClick : undefined} sx={badgeSx}>
        {loading ? <ThemedSmallLoader /> : children}
      </ThemedBadge>
      <Menu anchorEl={anchorEl} open={canUpdate && Boolean(anchorEl)} onClose={handleClose}>
        {menuItems}
      </Menu>
    </>
  );
};

export default EditableBadge;
